import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create contexts
const AppContext = createContext(null);
const AuthContext = createContext(null);

// API client with interceptors
const apiClient = axios.create({
  baseURL: API,
  timeout: 30000,
});

// Auth Provider
export const AuthProvider = ({ children }) => {
  const [groupAuth, setGroupAuth] = useState(() => {
    const saved = localStorage.getItem('groupAuth');
    return saved ? JSON.parse(saved) : null;
  });
  const [adminAuth, setAdminAuth] = useState(() => {
    const saved = sessionStorage.getItem('adminAuth');
    return saved ? JSON.parse(saved) : null;
  });

  const loginGroup = async (groupNumber) => {
    const response = await apiClient.post('/group/login', { group_number: groupNumber });
    const data = response.data;
    
    if (data.success) {
      const authData = {
        token: data.token,
        group: data.group,
        loggedInAt: new Date().toISOString()
      };
      setGroupAuth(authData);
      localStorage.setItem('groupAuth', JSON.stringify(authData));
    }
    
    return data;
  };

  const logoutGroup = () => {
    setGroupAuth(null);
    localStorage.removeItem('groupAuth');
  };

  const loginAdmin = async () => {
    const response = await apiClient.post('/admin/login', {});
    const data = response.data;
    
    if (data.success) {
      const authData = {
        token: data.token,
        loggedInAt: new Date().toISOString()
      };
      setAdminAuth(authData);
      sessionStorage.setItem('adminAuth', JSON.stringify(authData));
    }
    
    return data;
  };

  const logoutAdmin = () => {
    setAdminAuth(null);
    sessionStorage.removeItem('adminAuth');
  };

  return (
    <AuthContext.Provider value={{
      groupAuth,
      adminAuth,
      loginGroup,
      logoutGroup,
      loginAdmin,
      logoutAdmin,
      isGroupLoggedIn: !!groupAuth,
      isAdminLoggedIn: !!adminAuth
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// App Context Provider
export const AppProvider = ({ children }) => {
  const [config, setConfig] = useState(null);
  const [timeData, setTimeData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchConfig = useCallback(async () => {
    try {
      const response = await apiClient.get('/admin/config');
      setConfig(response.data);
    } catch (error) {
      console.error('Error fetching config:', error);
    }
  }, []);

  const fetchTime = useCallback(async () => {
    try {
      const response = await apiClient.get('/time');
      setTimeData(response.data);
    } catch (error) {
      console.error('Error fetching time:', error);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      await Promise.all([fetchConfig(), fetchTime()]);
      setLoading(false);
    };
    init();
    
    // Refresh time every minute
    const interval = setInterval(fetchTime, 60000);
    return () => clearInterval(interval);
  }, [fetchConfig, fetchTime]);

  return (
    <AppContext.Provider value={{
      config,
      timeData,
      loading,
      refreshConfig: fetchConfig,
      refreshTime: fetchTime,
      apiClient
    }}>
      {children}
    </AppContext.Provider>
  );
};

// Custom hooks
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

// API functions
export const api = {
  // Time
  getTime: () => apiClient.get('/time'),
  
  // Groups
  getGroups: () => apiClient.get('/groups'),
  getGroup: (groupId) => apiClient.get(`/groups/${groupId}`),
  
  // Campers
  getCampersByGroup: (groupId, rosterDate) => {
    const params = rosterDate ? { roster_date: rosterDate } : {};
    return apiClient.get(`/campers/group/${groupId}`, { params });
  },
  getCampersToday: () => apiClient.get('/campers/today'),
  syncCampers: () => apiClient.get('/campers/sync'),
  
  // Orders
  submitOrder: (orderData, orderDate) => {
    const params = orderDate ? { order_date: orderDate } : {};
    return apiClient.post('/orders/submit', orderData, { params });
  },
  getGroupOrder: (groupId, orderDate) => {
    const params = orderDate ? { order_date: orderDate } : {};
    return apiClient.get(`/orders/group/${groupId}`, { params });
  },
  clearDayOrders: (date, password) => apiClient.post('/orders/clear-date', { date, password }),
  adminOverrideOrder: (groupId, orderData) => apiClient.post(`/orders/admin-override/${groupId}`, orderData),
  
  // Admin Config
  getConfig: () => apiClient.get('/admin/config'),
  setFoodOfDay: (data) => apiClient.post('/admin/food-of-day', data),
  updateAlternatives: (alternatives) => apiClient.post('/admin/alternatives', alternatives),
  updateLunchPeriods: (periods) => apiClient.post('/admin/lunch-periods', periods),
  toggleCutoff: (enabled) => apiClient.post('/admin/cutoff-toggle', { enabled }),
  
  // Staff (sync only - for identifying counselors/CITs in groups)
  syncStaff: () => apiClient.post('/staff/sync'),
  getStaffMenu: () => apiClient.get('/staff/menu'),
  getStaffByGroup: (groupId) => apiClient.get(`/staff/group/${groupId}`),
  updateStaffMenu: (items) => apiClient.post('/admin/staff-menu', { items }),
  getMenuSchedule: () => apiClient.get('/admin/menu-schedule'),
  updateMenuSchedule: (schedule) => apiClient.post('/admin/menu-schedule', { schedule }),

  // Trips
  getTrips: () => apiClient.get('/trips'),
  getTrip: (id) => apiClient.get(`/trips/${id}`),
  deleteTrip: (id) => apiClient.delete(`/trips/${id}`),
  submitTripOrder: (tripId, data) => apiClient.post(`/trips/${tripId}/order`, data),
  getTripOrders: (tripId) => apiClient.get(`/trips/${tripId}/orders`),
  getTripOrdersReport: () => apiClient.get('/trips/orders/report'),
  
  // Reports
  getDailyReport: (date) => apiClient.get('/reports/daily', { params: { date } }),
  exportReportCSV: (date) => apiClient.get('/reports/export/csv', { params: { date } }),
  downloadReportPDF: (date, period) => apiClient.get('/reports/pdf', { params: { date, period }, responseType: 'blob' }),
  downloadTripReportPDF: (tripId) => apiClient.get('/trips/report/pdf', { params: { trip_id: tripId }, responseType: 'blob' }),
  getReportHistory: (days = 7) => apiClient.get('/reports/history', { params: { days } }),
  
  // Sync
  getSyncStatus: () => apiClient.get('/sync/status'),
  triggerSync: () => apiClient.get('/campers/sync'),
  
  // Admin
  resetDaily: () => apiClient.post('/admin/reset-daily'),
  getCronStatus: () => apiClient.get('/admin/cron-status'),

  // Dietary / Allergens
  getAllergenData: () => apiClient.get('/allergens/data'),
  getAllergenMap: () => apiClient.get('/admin/allergen-map'),
  addAllergenItem: (data) => apiClient.post('/admin/allergen-map', data),
  updateAllergenItem: (id, data) => apiClient.put(`/admin/allergen-map/${id}`, data),
  deleteAllergenItem: (id) => apiClient.delete(`/admin/allergen-map/${id}`),
  getAllergyReview: () => apiClient.get('/admin/allergy-review'),
  searchCampersAdmin: (q) => apiClient.get('/admin/camper-search', { params: { q } }),
  addDietaryOverride: (data) => apiClient.post('/admin/dietary-overrides', data),
  deleteDietaryOverride: (id) => apiClient.delete(`/admin/dietary-overrides/${id}`),
  getAllergenLog: () => apiClient.get('/admin/allergen-log'),
};

export { apiClient };
